// # Salary Management Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import { collection, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { FaUserTie, FaMoneyBillWave, FaCalendarAlt, FaChartLine, FaEdit, FaTrash, FaPlus, FaSave, FaTimes, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import './salary.css';
import { calculateShares } from '../payments/register_pay_calc';

// # Import team members from register_pay_calc.js
import { teamMembers } from '../payments/register_pay_calc';

const SalaryManagement = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [showAddSalaryModal, setShowAddSalaryModal] = useState(false);
  // # Add state for edit modal
  const [showEditSalaryModal, setShowEditSalaryModal] = useState(false);
  // # Add state for selected salary to edit
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [tutors, setTutors] = useState([]);
  
  // # Form data for new salary deduction
  const [formData, setFormData] = useState({
    receiver_type: 'admin', // or 'tutor'
    receiver_id: '',
    receiver_name: '',
    receiver_email: '',
    amount_egp: '',
    notes: ''
  });

  // # Form data for editing salary
  const [editFormData, setEditFormData] = useState({
    amount_egp: '',
    notes: ''
  });

  // # Add state for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // # Fetch tutors from database
  useEffect(() => {
    const fetchTutors = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data()
        }));

        // # Filter users to get tutors
        const tutorsData = usersData.filter(user => user.role === 'tutor').map(tutor => ({
          id: tutor.uid,
          name: `${tutor.firstName} ${tutor.lastName}`.trim(),
          email: tutor.email
        }));

        setTutors(tutorsData);
      } catch (error) {
        console.error('Error fetching tutors:', error);
      }
    };

    if (isAdmin) {
      fetchTutors();
    }
  }, [isAdmin]);

  // # Fetch salary history
  useEffect(() => {
    const fetchSalaryHistory = async () => {
      if (!isAdmin) return;
      
      try {
        setLoading(true);
        
        // # Get financial collection documents
        const financialCollectionRef = collection(db, 'financial');
        const financialSnapshot = await getDocs(financialCollectionRef);
        
        let allSalaries = [];
        
        // # Process each document in the financial collection
        for (const docSnapshot of financialSnapshot.docs) {
          const docData = docSnapshot.data();
          const yearMonth = docSnapshot.id; // # Year_Month format (e.g., 2025_03)
          
          // # Check if the document has a salaries map
          if (docData.salaries) {
            // # Process each salary entry
            Object.entries(docData.salaries).forEach(([salaryId, salaryData]) => {
              // # Format the salary data for our component
              allSalaries.push({
                id: salaryId,
                docId: yearMonth,
                timestamp: salaryData.metadata?.created_at,
                amount_egp: salaryData.amount_egp,
                notes: salaryData.metadata?.notes || salaryData.notes || '',
                receiver: salaryData.reciever, // # Note the spelling 'reciever' in the new structure
                register_admin: salaryData.register_admin,
                metadata: salaryData.metadata
              });
            });
          }
        }
        
        // # Sort by timestamp (newest first)
        allSalaries.sort((a, b) => {
          const dateA = new Date(a.timestamp || a.metadata?.created_at);
          const dateB = new Date(b.timestamp || b.metadata?.created_at);
          return dateB - dateA;
        });
        
        setSalaryHistory(allSalaries);
      } catch (error) {
        console.error('Error fetching salary history:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSalaryHistory();
  }, [isAdmin]);

  // # Handle receiver type change
  const handleReceiverTypeChange = (e) => {
    const receiverType = e.target.value;
    
    setFormData({
      ...formData,
      receiver_type: receiverType,
      receiver_id: '',
      receiver_name: '',
      receiver_email: ''
    });
  };

  // # Handle admin selection
  const handleAdminChange = (e) => {
    const adminId = e.target.value;
    const admin = teamMembers.find(member => member.id === adminId);
    
    setFormData({
      ...formData,
      receiver_id: adminId,
      receiver_name: admin ? admin.name : '',
      receiver_email: admin ? admin.email : ''
    });
    
    setSelectedAdmin(adminId);
  };

  // # Handle tutor selection
  const handleTutorChange = (e) => {
    const tutorId = e.target.value;
    const tutor = tutors.find(tutor => tutor.id === tutorId);
    
    setFormData({
      ...formData,
      receiver_id: tutorId,
      receiver_name: tutor ? tutor.name : '',
      receiver_email: tutor ? tutor.email : ''
    });
  };

  // # Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // # Handle edit form field changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // # Open edit modal with selected salary data
  const handleEditClick = (salary) => {
    setSelectedSalary(salary);
    setEditFormData({
      amount_egp: salary.amount_egp,
      notes: salary.notes || salary.metadata?.notes || ''
    });
    setShowEditSalaryModal(true);
  };

  // # Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.amount_egp) {
      alert('Please enter an amount');
      return;
    }
    
    try {
      const timestamp = new Date();
      
      // # Get the document reference
      const docRef = doc(db, 'financial', selectedSalary.docId);
      
      // # Get the current document data
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('Document does not exist');
      }
      
      const docData = docSnap.data();
      
      // # Update the salary data - using 'reciever' to match the new structure
      const updatedSalaryData = {
        ...docData.salaries[selectedSalary.id],
        amount_egp: Number(editFormData.amount_egp),
        metadata: {
          ...docData.salaries[selectedSalary.id].metadata,
          updated_at: timestamp.toISOString(),
          notes: editFormData.notes
        }
      };
      
      // # Update the document
      await setDoc(docRef, {
        salaries: {
          [selectedSalary.id]: updatedSalaryData
        }
      }, { merge: true });
      
      // # Update the salary history
      const updatedSalaryHistory = salaryHistory.map(salary => {
        if (salary.id === selectedSalary.id) {
          return {
            ...salary,
            amount_egp: Number(editFormData.amount_egp),
            notes: editFormData.notes,
            metadata: {
              ...salary.metadata,
              updated_at: timestamp.toISOString(),
              notes: editFormData.notes
            }
          };
        }
        return salary;
      });
      
      setSalaryHistory(updatedSalaryHistory);
      
      // # Close the modal
      setShowEditSalaryModal(false);
      setSelectedSalary(null);
      setEditFormData({
        amount_egp: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error updating salary payment:', error);
      alert('Failed to update salary payment. Please try again.');
    }
  };

  // # Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.receiver_id || !formData.amount_egp) {
      alert('Please select a receiver and enter an amount');
      return;
    }
    
    try {
      const timestamp = new Date();
      // # Generate ID using ISO timestamp for consistency
      const uniqueId = Math.random().toString(36).substring(2, 8);
      const salaryId = `salary_${timestamp.toISOString()}_${uniqueId}`;
      
      // # Get current year and month for document ID
      const docId = `${timestamp.getFullYear()}_${String(timestamp.getMonth() + 1).padStart(2, '0')}`;
      
      // # Create salary data according to the new financial structure
      const salaryData = {
        metadata: {
          created_at: timestamp.toISOString(),
          updated_at: timestamp.toISOString(),
          notes: formData.notes || ''
        },
        register_admin: {
          uid: user.uid,
          email: user.email,
          name: user.displayName || ''
        },
        // # Note: 'reciever' is spelled this way in the new structure (not 'receiver')
        reciever: {
          reciever_type: formData.receiver_type,
          uid: formData.receiver_id,
          email: formData.receiver_email,
          name: formData.receiver_name
        },
        amount_egp: Number(formData.amount_egp)
      };
      
      // # Add to financial collection under salaries
      const docRef = doc(db, 'financial', docId);
      await setDoc(docRef, {
        salaries: {
          [salaryId]: salaryData
        }
      }, { merge: true });
      
      // # Add to salary history and close modal
      setSalaryHistory([
        {
          id: salaryId,
          docId: docId,
          timestamp: salaryData.metadata.created_at,
          amount_egp: salaryData.amount_egp,
          notes: salaryData.metadata.notes,
          receiver: salaryData.reciever, // # Note: Using 'reciever' but storing as 'receiver' in local state
          register_admin: salaryData.register_admin,
          metadata: salaryData.metadata
        },
        ...salaryHistory
      ]);
      
      // # Reset form and close modal
      setFormData({
        receiver_type: 'admin',
        receiver_id: '',
        receiver_name: '',
        receiver_email: '',
        amount_egp: '',
        notes: ''
      });
      setShowAddSalaryModal(false);
    } catch (error) {
      console.error('Error adding salary payment:', error);
      alert('Failed to add salary payment. Please try again.');
    }
  };

  // # Filter salary history based on search query
  const filteredSalaries = salaryHistory.filter(salary => 
    salary.receiver?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    salary.notes?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // # Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      // # Get the document reference
      const docRef = doc(db, 'financial', selectedSalary.docId);
      
      // # Get the current document data
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('Document does not exist');
      }
      
      const docData = docSnap.data();
      
      // # Check if salaries exists in the document
      if (!docData.salaries || !docData.salaries[selectedSalary.id]) {
        throw new Error('Salary entry not found in document');
      }
      
      console.log('Deleting salary:', selectedSalary.id, 'from document:', selectedSalary.docId);
      console.log('Original document structure:', JSON.stringify(docData));
      
      // # Approach 1: Use deleteField() to remove the specific field
      try {
        await updateDoc(docRef, {
          [`salaries.${selectedSalary.id}`]: deleteDoc()
        });
        console.log('Approach 1: deleteField() completed');
      } catch (error) {
        console.error('Approach 1 failed:', error);
      }
      
      // # Double-check if the update was successful
      let updatedDocSnap = await getDoc(docRef);
      let updatedDocData = updatedDocSnap.data();
      
      if (updatedDocData.salaries && updatedDocData.salaries[selectedSalary.id]) {
        console.log('Salary still exists after Approach 1, trying Approach 2');
        
        // # Approach 2: Create a new salaries object without the deleted salary
        try {
          const updatedSalaries = {};
          
          // # Copy all salaries except the one to be deleted
          Object.entries(docData.salaries).forEach(([id, data]) => {
            if (id !== selectedSalary.id) {
              updatedSalaries[id] = data;
            }
          });
          
          console.log('Original salaries count:', Object.keys(docData.salaries).length);
          console.log('Updated salaries count:', Object.keys(updatedSalaries).length);
          
          // # Update the document with the new salaries object
          await setDoc(docRef, {
            salaries: updatedSalaries
          }, { merge: true });
          console.log('Approach 2: setDoc with merge completed');
        } catch (error) {
          console.error('Approach 2 failed:', error);
        }
        
        // # Check again if the update was successful
        updatedDocSnap = await getDoc(docRef);
        updatedDocData = updatedDocSnap.data();
        
        if (updatedDocData.salaries && updatedDocData.salaries[selectedSalary.id]) {
          console.log('Salary still exists after Approach 2, trying Approach 3');
          
          // # Approach 3: Create a completely new document with all fields except the salary to delete
          try {
            // # Create a deep copy of the document data
            const newDocData = JSON.parse(JSON.stringify(docData));
            
            // # Remove the specific salary entry
            if (newDocData.salaries) {
              delete newDocData.salaries[selectedSalary.id];
            }
            
            // # Replace the entire document
            await setDoc(docRef, newDocData);
            console.log('Approach 3: complete document replacement completed');
          } catch (error) {
            console.error('Approach 3 failed:', error);
            
            // # Note: If all client-side approaches fail, a Cloud Function would be the most reliable solution
            // # A Cloud Function can directly manipulate the Firestore database with admin privileges
            // # This would require implementing a Cloud Function that accepts the document ID and salary ID
            // # and performs the deletion server-side
            
            throw new Error('Failed to delete salary after multiple attempts: ' + error.message);
          }
        }
      }
      
      // # Final check
      const finalDocSnap = await getDoc(docRef);
      const finalDocData = finalDocSnap.data();
      
      if (finalDocData.salaries && finalDocData.salaries[selectedSalary.id]) {
        throw new Error('Failed to delete salary from database after all attempts.');
      }
      
      console.log('Salary successfully deleted from Firestore');
      
      // # Update the salary history in the UI
      const updatedSalaryHistory = salaryHistory.filter(
        salary => salary.id !== selectedSalary.id
      );
      
      setSalaryHistory(updatedSalaryHistory);
      
      // # Close the modals
      setShowDeleteConfirm(false);
      setShowEditSalaryModal(false);
      setSelectedSalary(null);
      
      // # Show success message
      alert('Salary payment deleted successfully');
      
    } catch (error) {
      console.error('Error deleting salary payment:', error);
      alert('Failed to delete salary payment: ' + error.message);
    }
  };

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  return (
    <div className="fnc_sal_page">
      <SharedHeader />
      
      <main className="fnc_sal_content">
        <div className="fnc_sal_header">
          <h1>Salary Management</h1>
          <div className="fnc_sal_header-controls">
            <button 
              className="fnc_sal_add-button"
              onClick={() => setShowAddSalaryModal(true)}
            >
              <FaPlus /> Record Salary Payment
            </button>
            <button 
              className="fnc_sal_back-button"
              onClick={() => navigate('/admin/financial')}
            >
              Back to Financial System
            </button>
          </div>
        </div>

        {/* Salary Stats Dashboard */}
        <div className="fnc_sal_stats-dashboard">
          <div className="fnc_sal_stats-grid">
            <div className="fnc_sal_stat-card">
              <FaMoneyBillWave className="fnc_sal_stat-icon" />
              <h3>Total Salary Payments</h3>
              <p className="fnc_sal_stat-value">
                {salaryHistory.length}
              </p>
            </div>
            <div className="fnc_sal_stat-card">
              <FaUserTie className="fnc_sal_stat-icon" />
              <h3>Total Amount Paid</h3>
              <p className="fnc_sal_stat-value">
                E£ {salaryHistory.reduce((sum, salary) => sum + Number(salary.amount_egp || 0), 0).toLocaleString()}
              </p>
            </div>
            <div className="fnc_sal_stat-card">
              <FaCalendarAlt className="fnc_sal_stat-icon" />
              <h3>Last Payment Date</h3>
              <p className="fnc_sal_stat-value">
                {salaryHistory.length > 0 
                  ? new Date(salaryHistory[0].timestamp || salaryHistory[0].metadata?.created_at).toLocaleDateString() 
                  : 'No payments yet'}
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="fnc_sal_search-container">
          <div className="fnc_sal_search-bar">
            <FaPlus className="fnc_sal_search-icon" />
            <input
              type="text"
              placeholder="Search by name or notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Salary History Table */}
        <div className="fnc_sal_table-container">
          {loading ? (
            <div className="fnc_sal_loading">Loading salary history...</div>
          ) : filteredSalaries.length === 0 ? (
            <div className="fnc_sal_no-data">
              {searchQuery 
                ? 'No salary payments match your search criteria' 
                : 'No salary payments recorded yet'}
            </div>
          ) : (
            <table className="fnc_sal_table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Payment To</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Notes</th>
                  <th>Recorded By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSalaries.map(salary => (
                  <tr key={salary.id}>
                    <td>{new Date(salary.timestamp || salary.metadata?.created_at).toLocaleDateString()}</td>
                    <td>{salary.receiver?.name}</td>
                    <td>{salary.receiver?.receiver_type}</td>
                    <td>E£ {Number(salary.amount_egp).toLocaleString()}</td>
                    <td>{salary.notes || salary.metadata?.notes}</td>
                    <td>{salary.register_admin?.name || salary.register_admin?.email}</td>
                    <td>
                      <button 
                        className="fnc_sal_edit-button"
                        onClick={() => handleEditClick(salary)}
                        title="Edit Salary"
                      >
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Add Salary Modal */}
      {showAddSalaryModal && (
        <div className="fnc_sal_modal-overlay">
          <div className="fnc_sal_modal">
            <div className="fnc_sal_modal-header">
              <h2><FaMoneyBillWave /> Record Salary Payment</h2>
              <button 
                className="fnc_sal_modal-close"
                onClick={() => setShowAddSalaryModal(false)}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>
            <form className="fnc_sal_modal-form" onSubmit={handleSubmit}>
              {/* Receiver Type Selection */}
              <div className="fnc_sal_form-group">
                <div className="fnc_sal_receiver-type-selector">
                  <label className={`fnc_sal_receiver-type-option ${formData.receiver_type === 'admin' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="receiver_type"
                      value="admin"
                      checked={formData.receiver_type === 'admin'}
                      onChange={handleReceiverTypeChange}
                    />
                    <FaUserTie /> Admin
                  </label>
                  <label className={`fnc_sal_receiver-type-option ${formData.receiver_type === 'tutor' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="receiver_type"
                      value="tutor"
                      checked={formData.receiver_type === 'tutor'}
                      onChange={handleReceiverTypeChange}
                    />
                    <FaUserTie /> Tutor
                  </label>
                </div>
              </div>

              {/* Receiver Selection - Conditional based on type */}
              {formData.receiver_type === 'admin' ? (
                <div className="fnc_sal_form-group">
                  <label>Select Admin</label>
                  <select
                    name="receiver_id"
                    value={formData.receiver_id}
                    onChange={handleAdminChange}
                    required
                  >
                    <option value="">Choose an admin...</option>
                    {teamMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="fnc_sal_form-group">
                  <label>Select Tutor</label>
                  <select
                    name="receiver_id"
                    value={formData.receiver_id}
                    onChange={handleTutorChange}
                    required
                  >
                    <option value="">Choose a tutor...</option>
                    {tutors.map(tutor => (
                      <option key={tutor.id} value={tutor.id}>
                        {tutor.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="fnc_sal_form-group">
                <label>Payment Amount</label>
                <input
                  type="number"
                  name="amount_egp"
                  value={formData.amount_egp}
                  onChange={handleChange}
                  placeholder="Enter amount in EGP"
                  required
                  min="1"
                  step="0.01"
                />
              </div>
              <div className="fnc_sal_form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add any additional notes here..."
                  rows="2"
                />
              </div>
              <div className="fnc_sal_form-actions">
                <button 
                  type="button" 
                  className="fnc_sal_cancel-button"
                  onClick={() => setShowAddSalaryModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="fnc_sal_submit-button"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Salary Modal */}
      {showEditSalaryModal && selectedSalary && (
        <div className="fnc_sal_modal-overlay">
          <div className="fnc_sal_modal">
            <div className="fnc_sal_modal-header">
              <h2><FaEdit /> Edit Salary Payment</h2>
              <button 
                className="fnc_sal_modal-close"
                onClick={() => {
                  setShowEditSalaryModal(false);
                  setSelectedSalary(null);
                }}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>
            <form className="fnc_sal_modal-form" onSubmit={handleEditSubmit}>
              <div className="fnc_sal_form-group">
                <label>Payment To</label>
                <input
                  type="text"
                  value={selectedSalary.receiver?.name}
                  disabled
                  className="fnc_sal_input-disabled"
                />
              </div>
              <div className="fnc_sal_form-group">
                <label>Type</label>
                <input
                  type="text"
                  value={selectedSalary.receiver?.receiver_type}
                  disabled
                  className="fnc_sal_input-disabled"
                />
              </div>
              <div className="fnc_sal_form-group">
                <label>Payment Amount</label>
                <input
                  type="number"
                  name="amount_egp"
                  value={editFormData.amount_egp}
                  onChange={handleEditChange}
                  placeholder="Enter amount in EGP"
                  required
                  min="1"
                  step="0.01"
                />
              </div>
              <div className="fnc_sal_form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={editFormData.notes}
                  onChange={handleEditChange}
                  placeholder="Add any additional notes here..."
                  rows="2"
                />
              </div>
              <div className="fnc_sal_form-actions">
                <button 
                  type="button" 
                  className="fnc_sal_delete-button"
                  onClick={() => setShowDeleteConfirm(true)}
                  title="Delete this salary payment"
                >
                  <FaTrash /> Delete
                </button>
                <div className="fnc_sal_right-actions">
                  <button 
                    type="button" 
                    className="fnc_sal_cancel-button"
                    onClick={() => {
                      setShowEditSalaryModal(false);
                      setSelectedSalary(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="fnc_sal_submit-button"
                  >
                    <FaSave /> Save Changes
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedSalary && (
        <div className="fnc_sal_modal-overlay">
          <div className="fnc_sal_modal fnc_sal_confirm-modal">
            <div className="fnc_sal_modal-header">
              <h2><FaExclamationTriangle /> Confirm Deletion</h2>
              <button 
                className="fnc_sal_modal-close"
                onClick={() => setShowDeleteConfirm(false)}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="fnc_sal_confirm-content">
              <p>Are you sure you want to delete this salary payment?</p>
              <p><strong>Payment to:</strong> {selectedSalary.receiver?.name}</p>
              <p><strong>Amount:</strong> E£ {Number(selectedSalary.amount_egp).toLocaleString()}</p>
              <p><strong>Date:</strong> {new Date(selectedSalary.timestamp || selectedSalary.metadata?.created_at).toLocaleDateString()}</p>
              <p className="fnc_sal_warning-text"><FaExclamationTriangle /> This action cannot be undone!</p>
            </div>
            <div className="fnc_sal_confirm-actions">
              <button 
                type="button" 
                className="fnc_sal_cancel-button"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="fnc_sal_delete-confirm-button"
                onClick={handleDeleteConfirm}
              >
                <FaTrash /> Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <DashboardFooter />
    </div>
  );
};

export const getSalaryDeductions = async () => {
  try {
    // # Get financial collection documents
    const financialCollectionRef = collection(db, 'financial');
    const financialSnapshot = await getDocs(financialCollectionRef);
    
    let allDeductions = [];
    
    // # Process each document in the financial collection
    for (const docSnapshot of financialSnapshot.docs) {
      const docData = docSnapshot.data();
      const yearMonth = docSnapshot.id; // # Year_Month format (e.g., 2025_03)
      
      // # Check if the document has a salaries map
      if (docData.salaries) {
        // # Process each salary entry
        Object.entries(docData.salaries).forEach(([salaryId, salaryData]) => {
          // # Format the salary data for our component
          allDeductions.push({
            id: salaryId,
            docId: yearMonth,
            timestamp: salaryData.metadata?.created_at,
            amount_egp: salaryData.amount_egp,
            notes: salaryData.metadata?.notes || salaryData.notes || '',
            receiver: salaryData.reciever, // # Note the spelling 'reciever' in the new structure
            register_admin: salaryData.register_admin,
            metadata: salaryData.metadata
          });
        });
      }
    }
    
    // # Sort by timestamp (newest first)
    allDeductions.sort((a, b) => {
      const dateA = new Date(a.timestamp || a.metadata?.created_at);
      const dateB = new Date(b.timestamp || b.metadata?.created_at);
      return dateB - dateA;
    });
    
    return allDeductions;
  } catch (error) {
    console.error('Error fetching salary deductions:', error);
    return [];
  }
};

export default SalaryManagement; 